import { createContext, useContext, useCallback, useState, useEffect, ReactNode } from 'react'
import { useSession } from 'next-auth/react'
import { useCompanyContext } from '@/lib/context/companyContext'
import { PERMISSIONS } from '@/lib/constants/permissions'
import { getMLSSelectSuggestions } from '@/app/service/experienceService'
import { extractApiErrorDetails, isExpApiErrorObject } from '@/app/lib/errors/apiError'
import { ExpApiErrorCode } from '@/app/lib/errors/models/errorTypes'
import { MLSSystem } from '@/app/service/models/recruitersdetails'
import { useRetry } from './useRetry'

type MLSSystemsContextType = {
	error: boolean
	mlsSystems: MLSSystem[]
	isLoading: boolean
	refetch: () => void
	getFilteredMLSSystems: (search: string) => MLSSystem[]
}

const MLSSystemsContext = createContext<MLSSystemsContextType | undefined>(undefined)

export function useMLSSystemsProvider() {
	const { data: session } = useSession()
	const { selectedCompanyId: partyId, permissions } = useCompanyContext()
	const hasRecruitAccess = permissions?.includes(PERMISSIONS.RECRUITING_USER) ?? false
	const { retryWithBackoff } = useRetry()

	const [mlsSystems, setMlsSystems] = useState<MLSSystem[] | null>(null)
	const [isMlsRefreshError, setIsMlsRefreshError] = useState(false)

	const refreshMlsSystems = useCallback(
		async (companyId: string): Promise<void> => {
			if (!companyId) return

			try {
				// MLS systems call is only needed by the MLS search page, but a failure here
				// sets isMlsRefreshError which blocks the entire recruiting layout —
				// pipeline and nurturing included. Retrying first means a transient
				// upstream 500 won't unnecessarily take down unrelated pages.
				const response = (await retryWithBackoff(() =>
					getMLSSelectSuggestions(companyId, { token: session?.accessToken })
				)) as { mlsSystems?: MLSSystem[] }
				setMlsSystems(response?.mlsSystems ?? [])
			} catch (error) {
				setMlsSystems([])

				const errorDetails = extractApiErrorDetails(error, 'Failed to fetch MLS systems')
				if (isExpApiErrorObject(errorDetails) && errorDetails.errorCd === ExpApiErrorCode.SourceNotFoundError) {
					return
				}
				setIsMlsRefreshError(true)
				console.error('Failed to fetch MLS systems:', errorDetails)
			}
		},
		[session?.accessToken, retryWithBackoff]
	)

	// Fetch MLS systems when the user has recruiting access.
	// permissions (from UserContext) is the trigger — it updates AFTER
	// UserContext refreshes for a new company, so selectedCompanyId in the
	// closure already holds the correct value by that point.
	useEffect(() => {
		if (partyId && session?.accessToken && hasRecruitAccess) {
			refreshMlsSystems(partyId)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [session?.accessToken, hasRecruitAccess])

	const fetchMLSSystems = useCallback(async () => {
		await refreshMlsSystems(partyId as string)
	}, [partyId, refreshMlsSystems])

	const getFilteredMLSSystems = useCallback(
		(search: string) => {
			const systems = mlsSystems ?? []
			if (!search) return systems
			const lowerSearch = search.toLowerCase()
			return systems.filter(
				sys => sys.mlsNm.toLowerCase().includes(lowerSearch) || sys.mlsId.toLowerCase().includes(lowerSearch)
			)
		},
		[mlsSystems]
	)

	return {
		error: isMlsRefreshError,
		isLoading: mlsSystems === null,
		mlsSystems: mlsSystems ?? [],
		refetch: fetchMLSSystems,
		getFilteredMLSSystems,
	}
}

// Context Provider component
export function MLSSystemsProvider({
	children,
}: Readonly<{
	children: ReactNode
}>) {
	const value = useMLSSystemsProvider()
	return <MLSSystemsContext.Provider value={value}>{children}</MLSSystemsContext.Provider>
}

// Context consumer hook
export function useMLSSystemsContext() {
	const ctx = useContext(MLSSystemsContext)
	if (!ctx) throw new Error('useMLSSystemsContext must be used within MLSSystemsProvider')
	return ctx
}
